<template>
   <!-- Desktop header bar -->
   <header
      class="__container h-(--header-height) fixed inset-x-0 z-[70] translate-y-0 transition-transform duration-300"
      :class="{ '!-translate-y-full': !isHeaderVisible && !isMobileMenuOpen }"
   >
      <div
         class="rounded-base mt-7 flex h-[70px] w-full items-center gap-4 bg-white px-4 sm:px-8"
         :class="{ __shadow: !isMobileMenuOpen }"
      >
         <div class="flex items-center gap-12">
            <NuxtLink :to="$localePath('/')" class="pt-1">
               <Icon name="logotype" class="h-full w-[120px]" />
            </NuxtLink>

            <ul
               class="p-l text-grey hidden items-center gap-8 xl:flex"
               :class="{ '!hidden': isMobileMenuOpen }"
            >
               <li v-for="item in navItems" :key="item.key" class="__clickable shrink-0">
                  <NuxtLink :to="$localePath(item.to)" :class="{ 'text-primary': isActive(item) }">
                     {{ $t(`web.components.header.${item.key}`) }}
                  </NuxtLink>
               </li>
            </ul>
         </div>

         <div class="ml-auto flex items-center gap-5">
            <HeaderLanguage class="hidden xl:flex" :class="{ '!hidden': isMobileMenuOpen }" />

            <div class="flex items-center gap-2">
               <NuxtLink :to="$localePath('/store-redirect')">
                  <Button :class="{ '!hidden': isMobileMenuOpen }">
                     {{ $t('common.download-app') }}
                  </Button>
               </NuxtLink>

               <div
                  class="__clickable bg-secondary rounded-small relative z-50 flex size-10 shrink-0 flex-col items-center justify-center gap-1 xl:hidden"
                  @click="toggleMobileMenu"
               >
                  <div
                     class="bg-primary h-[2px] w-[20px] transition-all duration-300"
                     :class="{ 'translate-y-1.5 rotate-45': isMobileMenuOpen }"
                  />
                  <div
                     class="bg-primary h-[2px] w-[20px] transition-all duration-300"
                     :class="{ 'opacity-0': isMobileMenuOpen }"
                  />
                  <div
                     class="bg-primary h-[2px] w-[20px] transition-all duration-300"
                     :class="{ '-translate-y-1.5 -rotate-45': isMobileMenuOpen }"
                  />
               </div>
            </div>
         </div>
      </div>
   </header>

   <!-- Mobile menu overlay -->
   <div
      class="pt-(--header-height) fixed inset-y-0 left-0 z-[61] w-full translate-x-[-100%] bg-white px-4 py-6 transition-transform duration-300 sm:px-7 xl:hidden"
      :class="{ '!translate-x-0': isMobileMenuOpen }"
   >
      <div class="__container flex h-full flex-col pt-4">
         <ul class="p-l text-grey flex flex-col gap-3">
            <li v-for="item in navItems" :key="item.key" class="__clickable shrink-0">
               <NuxtLink :to="$localePath(item.to)">
                  {{ $t(`web.components.header.${item.key}`) }}
               </NuxtLink>
            </li>
         </ul>

         <div class="mt-auto flex flex-col gap-4">
            <Separator />
            <div class="flex justify-between gap-4 max-sm:flex-col">
               <HeaderLanguage />
               <NuxtLink :to="$localePath('/store-redirect')">
                  <Button grow large>{{ $t('common.download-app') }}</Button>
               </NuxtLink>
            </div>
         </div>
      </div>
   </div>
</template>

<script lang="ts" setup>
const route = useRoute()
const isMobileMenuOpen = ref(false)
const isHeaderVisible = ref(true)
let lastScrollY = 0
let ticking = false

const navItems = [
   { key: 'homepage', to: '/' },
   { key: 'how-it-works', to: '/#how-it-works' },
   { key: 'faq', to: '/#faq' },
   { key: 'fishing-in-south-tyrol', to: '/#fishing-in-south-tyrol' },
   { key: 'contacts', to: '/#contacts' },
]

const isActive = (item: (typeof navItems)[number]) => {
   if (item.to === '/') return (!route.hash || route.hash === '') && route.path === '/'
   return route.hash === item.to.replace('/', '')
}

const toggleMobileMenu = () => {
   isMobileMenuOpen.value = !isMobileMenuOpen.value
   if (import.meta.client) {
      document.body.style.overflow = isMobileMenuOpen.value ? 'hidden' : ''
   }
}

const closeMobileMenu = () => {
   if (isMobileMenuOpen.value) {
      isMobileMenuOpen.value = false
      if (import.meta.client) document.body.style.overflow = ''
   }
}

watch(() => route.fullPath, closeMobileMenu)

const handleScroll = () => {
   if (ticking) return
   ticking = true
   window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY
      isHeaderVisible.value = currentScrollY <= 100 || currentScrollY < lastScrollY
      lastScrollY = currentScrollY
      ticking = false
   })
}

const handleResize = () => {
   if (import.meta.client && window.innerWidth >= 1280) closeMobileMenu()
}

onMounted(() => {
   if (import.meta.client) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('resize', handleResize, { passive: true })
   }
})

onUnmounted(() => {
   if (import.meta.client) {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
      document.body.style.overflow = ''
   }
})
</script>
